'use client';
import {
  BanknoteArrowDown,
  BanknoteArrowUp,
  BarChart3,
  Barrel,
  Building2,
  Calendar,
  Car,
  ChevronDown,
  ChevronRight,
  Code,
  Compass,
  CreditCard,
  Database,
  Dot,
  Droplets,
  FileText,
  Fuel,
  Gauge,
  Home,
  KeyRound,
  LogOut,
  MapPin,
  Receipt,
  ReceiptText,
  Settings,
  Stamp,
  Truck,
  UserCheck,
  Users,
  Wallet,
} from 'lucide-react';

import { CompanySelector } from '@/components/common/company-selector';
import { CompanySwitcher } from '@/components/common/company-switcher';
import { Button } from '@/components/ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar';
import { APPROVAL_ENTRY_MENU_KEYS } from '@/lib/approval-client';
import type { Tab } from '@/components/layout/tab-header';
import { menuCatalog } from '@/config/menu-catalog';
import { useAuth } from '@/contexts/auth-context';
import { useRBAC } from '@/contexts/rbac-context';
import { useToast } from '@/hooks/use-toast';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type IconComponent = React.ComponentType<{ className?: string }>;

// Menu item interface used for sidebar rendering (catalog + icon).
interface MenuItem {
  id: string;
  title: string;
  icon: IconComponent;
  children?: MenuItem[];
}

// 섹션/항목 id → 아이콘 매핑(렌더 전용). 카탈로그에 없는 id는 기본 아이콘 사용.
/**
 * 탭 쉘 밖 새 창으로 여는 화면. 섹션 id(전자결재)와 항목 id(주유소 지도)를 한 표에서 받는다.
 * 전자결재는 좌측 레일로 결재함·기안함·관리를 오가는 SPA 라 사이드바에 항목을 늘어놓지 않는다
 * (권한 키는 그대로 5개다 - 역할세트 화면이 카탈로그로 부여한다).
 */
const WINDOW_PATH: Record<string, string> = { 'station-map': '/station-map', approval: '/approvals' };

/** 새 창은 조직 세그먼트를 유지해야 로그인 상태로 열린다 */
function openStandaloneWindow(path: string) {
  window.open(`/${window.location.pathname.split('/')[1]}${path}`, '_blank', 'noopener,noreferrer');
}

const sectionIcons: Record<string, IconComponent> = {
  management: Users,
  aggregation: BarChart3,
  inout: BanknoteArrowDown,
  stock: Barrel,
  sales: Calendar,
  finance: Receipt,
  master: Database,
  system: Code,
};

export const itemIcons: Record<string, IconComponent> = {
  dashboard: Home,
  'station-map': MapPin,
  'user-approval': UserCheck,
  'user-management': Users,
  'organization-management': Building2,
  'station-management': Fuel,
  'menu-role-sets': KeyRound,
  salesinfo: Code,
  salestotal: BarChart3,
  cardsales: CreditCard,
  selfsales: Fuel,
  timesale: BarChart3,
  oct: Receipt,
  delivery: Truck,
  money: BanknoteArrowDown,
  purchase: BanknoteArrowUp,
  tankjg: Barrel,
  'tlg-realtime': Gauge,
  'close-stat': Calendar,
  aggregate: BarChart3,
  'car-wash': Car,
  invoice: FileText,
  'tax-invoice': ReceiptText,
  account: Wallet,
  car: Car,
  'cust-card': CreditCard,
  'cust-cost': BarChart3,
  nozzle: Fuel,
  cust: Building2,
  oil: Droplets,
  tank: Database,
  'data-transfer-status': BarChart3,
};

// 사이드바 표시 구조. 카탈로그(menu-catalog)는 권한 키 표라 건드리지 않고 보이는 묶음만 여기서 만든다.
const menuStructure: MenuItem[] = [
  { id: 'dashboard', title: 'Dashboard', icon: Home },
  {
    id: 'shortcut',
    title: '바로가기',
    icon: Compass,
    children: [
      { id: 'station-map', title: '주유소 지도', icon: MapPin },
      { id: 'approval', title: '전자결재', icon: Stamp },
    ],
  },
  // 나머지는 카탈로그 그대로. main·approval 섹션은 위에서 다시 묶었다
  ...menuCatalog
    .filter(section => section.sectionId !== 'main' && section.sectionId !== 'approval')
    .map(section => ({
      id: section.sectionId,
      title: section.sectionTitle,
      icon: sectionIcons[section.sectionId] ?? Settings,
      // 동작 권한(item.actions)은 이동할 페이지가 없으므로 사이드바에는 화면 항목만 올린다.
      children: section.items.map(item => ({
        id: item.id,
        title: item.title,
        icon: itemIcons[item.id] ?? Dot,
      })),
    })),
];

interface AppSidebarProps {
  onOpenTab: (item: Tab) => void;
}

export function AppSidebar({ onOpenTab }: AppSidebarProps) {
  const { user, logout } = useAuth();
  const { canAccessMenu, isSuperAdmin } = useRBAC();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState<string[]>(['shortcut']); // 바로가기는 펼쳐진 상태로 시작


  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      toast({
        title: '로그아웃 실패',
        description: '로그아웃 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    }
  };

  // 메뉴 접근 판정: 항목은 canAccessMenu(item.id), 섹션은 접근 가능한 자식이 없으면 숨김
  const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
    return items
      .map(item => {
        if (item.children) {
          const filteredChildren = filterMenuItems(item.children);
          // Don't show parent if no accessible children
          if (filteredChildren.length === 0) {
            return null;
          }
          return { ...item, children: filteredChildren };
        }
        // 전자결재는 항목 하나에 키가 5개다 - 하나라도 있으면 보인다
        const visible =
          item.id === 'approval' ? APPROVAL_ENTRY_MENU_KEYS.some(canAccessMenu) : canAccessMenu(item.id);
        return visible ? item : null;
      })
      .filter(Boolean) as MenuItem[];
  };

  // Apply access filtering to menu structure
  const filteredMenuStructure = useMemo(() => {
    return filterMenuItems(menuStructure);
  }, [canAccessMenu]);

  return (
    <Sidebar>
      <SidebarHeader>
        <div className='flex items-center gap-2 px-2 py-2'>
          <div className='flex aspect-square size-8 items-center justify-center rounded-lg border-2 text-sidebar-primary-foreground bg-white'>
            <img src='/wjpnclogo.png' alt='WJPNC' width={32} height={32} />
          </div>
          <div className='flex flex-col gap-0.5 leading-none'>
            <span className='font-semibold'>WJPNC</span>
            {user && (
              <span className='text-xs text-sidebar-foreground/70'>
                {user?.username}
              </span>
            )}
          </div>
        </div>

        {/* 다중 소속 회사 전환(재로그인) */}
        <CompanySwitcher className='px-2 pb-2' />

        {/* 슈퍼어드민 전용 회사 선택 */}
        <CompanySelector className='px-2 pb-2' />

        {/* 슈퍼어드민 전용 회사(테넌트) 관리 진입 */}
        {isSuperAdmin && (
          <div className='px-2 pb-2'>
            <Button
              variant='outline'
              size='sm'
              className='w-full justify-start bg-transparent'
              onClick={() => navigate('/admin/company')}
            >
              <Building2 className='size-4 mr-2' />
              회사 관리
            </Button>
          </div>
        )}
      </SidebarHeader>
      <SidebarContent className='px-[10px]'>
        <SidebarMenu className='gap-[5px]'>
          {filteredMenuStructure.map(category => {
            const windowPath = WINDOW_PATH[category.id];
            return (
            <div key={category.id}>
              {/* 최상단 버튼. 자식이 없으면 화면을 바로 열고, 있으면 펼친다 */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => {
                    if (windowPath) openStandaloneWindow(windowPath);
                    else if (category.children) toggleExpanded(category.id);
                    else onOpenTab(category);
                  }}
                  title={windowPath ? '새 창에서 열기' : undefined}
                  aria-expanded={category.children ? expandedItems.includes(category.id) : undefined}
                  className='cursor-pointer w-full justify-between text-base'
                >
                  <div className='flex items-center gap-2'>
                    <category.icon className='size-4' />
                    <span>{category.title}</span>
                  </div>
                  {category.children &&
                    (expandedItems.includes(category.id) ? (
                      <ChevronDown className='size-4' />
                    ) : (
                      <ChevronRight className='size-4' />
                    ))}
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* 서브 메뉴 아이템들 */}
              {expandedItems.includes(category.id) && category.children && (
                <div className='ml-3'>
                  {category.children.map(item => (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        onClick={() => {
                          const path = WINDOW_PATH[item.id];
                          if (path) openStandaloneWindow(path);
                          else onOpenTab(item);
                        }}
                        className='cursor-pointer'
                      >
                        <Dot className='size-8' />
                        <span className='text-sm'>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </div>
              )}
            </div>
            );
          })}
        </SidebarMenu>
      </SidebarContent>
      <SidebarFooter>
        <div className='px-2 py-2'>
          <Button
            variant='outline'
            size='sm'
            className='w-full justify-start bg-transparent hover:text-gray-700 hover:bg-gray-100'
            onClick={handleLogout}
          >
            <LogOut className='size-4 mr-2' />
            로그아웃
          </Button>
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
